<script>
import Modal from 'dashboard/components/Modal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import ConversationAPI from 'dashboard/api/conversations';
import SearchAPI from 'dashboard/api/search';
import messageAPI from 'dashboard/api/inbox/message';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'ForwardMessageModal',
  components: {
    Modal,
    NextButton,
    ChannelIcon,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      show: true,
      recentConversations: [],
      searchResults: [],
      loading: false,
      forwarding: false,
      selectedConversationIds: new Set(),
      searchQuery: '',
      searchTimer: null,
      searchToken: 0,
    };
  },
  computed: {
    displayedConversations() {
      return this.searchQuery.trim()
        ? this.searchResults
        : this.recentConversations;
    },
    allFilteredSelected() {
      const list = this.displayedConversations;
      return (
        list.length > 0 && list.every(c => this.selectedConversationIds.has(c.id))
      );
    },
    channelLabel() {
      return conversation => {
        const channel = conversation.meta?.channel || '';
        return channel.replace(/^Channel::/, '');
      };
    },
  },
  watch: {
    searchQuery(value) {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      const q = value.trim();
      if (!q) {
        this.searchResults = [];
        this.loading = false;
        return;
      }
      this.loading = true;
      this.searchTimer = setTimeout(() => this.runSearch(q), 300);
    },
  },
  async mounted() {
    await this.fetchRecentConversations();
  },
  beforeUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    async fetchRecentConversations() {
      this.loading = true;
      try {
        const response = await ConversationAPI.get();
        this.recentConversations = response.data?.data?.payload || [];
      } catch (error) {
        this.recentConversations = [];
      } finally {
        this.loading = false;
      }
    },
    async runSearch(q) {
      const token = ++this.searchToken;
      try {
        const response = await SearchAPI.conversations({ q });
        if (token !== this.searchToken) return;
        const raw = response.data?.payload?.conversations || [];
        this.searchResults = raw.map(this.normalizeSearchResult);
      } catch (error) {
        if (token !== this.searchToken) return;
        this.searchResults = [];
      } finally {
        if (token === this.searchToken) this.loading = false;
      }
    },
    normalizeSearchResult(item) {
      // Search API returns { id, contact, inbox, message, agent, ... }
      // ConversationAPI.get() returns { id, meta: { sender, channel, ... } }
      // Normalize to the ConversationAPI shape so the template reuses one path.
      return {
        id: item.id,
        meta: {
          sender: item.contact || {},
          channel: item.inbox?.channel_type || item.inbox?.name || '',
        },
      };
    },
    handleClose() {
      this.show = false;
      this.onClose();
    },
    toggleConversation(conversationId) {
      if (this.selectedConversationIds.has(conversationId)) {
        this.selectedConversationIds.delete(conversationId);
      } else {
        this.selectedConversationIds.add(conversationId);
      }
      // trigger reactivity for Set
      this.selectedConversationIds = new Set(this.selectedConversationIds);
    },
    isSelected(conversationId) {
      return this.selectedConversationIds.has(conversationId);
    },
    handleSelectAll() {
      const list = this.displayedConversations;
      if (this.allFilteredSelected) {
        list.forEach(c => this.selectedConversationIds.delete(c.id));
      } else {
        list.forEach(c => this.selectedConversationIds.add(c.id));
      }
      this.selectedConversationIds = new Set(this.selectedConversationIds);
    },
    senderInitial(conversation) {
      const name = conversation.meta?.sender?.name || '';
      return name.trim().charAt(0).toUpperCase() || '#';
    },
    async handleForward() {
      if (this.selectedConversationIds.size === 0) return;
      this.forwarding = true;
      try {
        const conversationId =
          this.message.conversation_id || this.message.conversationId;
        const messageId = this.message.id;
        const targetConversationIds = Array.from(this.selectedConversationIds);
        await messageAPI.forwardMessage(
          conversationId,
          messageId,
          targetConversationIds
        );
        useAlert(this.$t('CONVERSATION.FORWARD_MESSAGE.SUCCESS'));
        this.handleClose();
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error ||
          error?.message ||
          this.$t('CONVERSATION.FORWARD_MESSAGE.ERROR');
        useAlert(errorMessage);
      } finally {
        this.forwarding = false;
      }
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="handleClose">
    <div class="flex w-[32rem] max-w-full flex-col gap-4 p-6">
      <header class="flex flex-col gap-1">
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ $t('CONVERSATION.FORWARD_MESSAGE.DESCRIPTION') }}
        </h3>
        <p class="text-xs text-n-slate-11">
          {{ $t('CONVERSATION.FORWARD_MESSAGE.SELECT_CONVERSATION') }}
          ({{ selectedConversationIds.size }}/{{ displayedConversations.length }})
        </p>
      </header>

      <div class="flex items-center justify-between">
        <span class="text-[11px] font-semibold uppercase tracking-wide text-n-slate-11">
          {{ $t('CONVERSATION.FORWARD_MESSAGE.CONVERSATIONS_LABEL') }}
        </span>
        <button
          v-if="displayedConversations.length > 0"
          type="button"
          class="text-xs font-medium text-n-blue-text hover:text-n-blue-11 transition"
          @click="handleSelectAll"
        >
          {{
            allFilteredSelected
              ? $t('CONVERSATION.FORWARD_MESSAGE.DESELECT_ALL')
              : $t('CONVERSATION.FORWARD_MESSAGE.SELECT_ALL')
          }}
        </button>
      </div>

      <div class="relative">
        <span
          class="i-ri-search-line size-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-n-slate-10"
        />
        <input
          v-model="searchQuery"
          type="search"
          :placeholder="$t('CONVERSATION.FORWARD_MESSAGE.SEARCH_PLACEHOLDER')"
          class="w-full rounded-lg border border-n-weak bg-n-alpha-black2 pl-9 pr-3 py-2 text-xs text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-blue-border focus:outline-none"
        />
      </div>

      <div v-if="loading" class="flex items-center justify-center py-10">
        <span class="spinner" />
      </div>

      <div
        v-else-if="!searchQuery.trim() && recentConversations.length === 0"
        class="rounded-lg border border-dashed border-n-weak bg-n-alpha-1 py-10 text-center"
      >
        <p class="text-xs text-n-slate-11">
          {{ $t('CONVERSATION.FORWARD_MESSAGE.NO_CONVERSATIONS') }}
        </p>
      </div>

      <div
        v-else-if="displayedConversations.length === 0"
        class="rounded-lg border border-dashed border-n-weak bg-n-alpha-1 py-8 text-center"
      >
        <p class="text-xs text-n-slate-11">
          {{ $t('CONVERSATION.FORWARD_MESSAGE.NO_RESULTS') }}
        </p>
      </div>

      <ul
        v-else
        class="flex max-h-80 flex-col gap-1 overflow-y-auto rounded-lg border border-n-weak bg-n-alpha-1 p-1"
      >
        <li
          v-for="conversation in displayedConversations"
          :key="conversation.id"
          :class="[
            'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 transition',
            isSelected(conversation.id)
              ? 'bg-n-alpha-3 hover:bg-n-alpha-3'
              : 'hover:bg-n-alpha-2',
          ]"
          @click="toggleConversation(conversation.id)"
        >
          <input
            type="checkbox"
            :checked="isSelected(conversation.id)"
            class="h-4 w-4 cursor-pointer rounded border-n-weak accent-n-blue-9"
            @click.stop="toggleConversation(conversation.id)"
          />
          <div
            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-n-alpha-3 text-xs font-semibold text-n-slate-12"
          >
            {{ senderInitial(conversation) }}
          </div>
          <div class="min-w-0 flex-1">
            <p class="truncate text-sm font-medium text-n-slate-12">
              {{ conversation.meta?.sender?.name || `#${conversation.id}` }}
            </p>
            <p class="truncate text-[11px] text-n-slate-11">
              <span v-if="channelLabel(conversation)">
                {{ channelLabel(conversation) }} ·
              </span>
              #{{ conversation.id }}
            </p>
          </div>
        </li>
      </ul>

      <p class="text-xs text-n-slate-11">
        {{
          $t('CONVERSATION.FORWARD_MESSAGE.SUMMARY', {
            count: selectedConversationIds.size,
            total: displayedConversations.length,
          })
        }}
      </p>

      <footer class="flex justify-end gap-2 pt-1">
        <NextButton
          faded
          slate
          sm
          :label="$t('CONVERSATION.FORWARD_MESSAGE.CANCEL')"
          @click="handleClose"
        />
        <NextButton
          solid
          blue
          sm
          :label="$t('CONVERSATION.FORWARD_MESSAGE.FORWARD')"
          :disabled="selectedConversationIds.size === 0 || forwarding"
          :is-loading="forwarding"
          @click="handleForward"
        />
      </footer>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
.spinner {
  @apply inline-block h-6 w-6 animate-spin rounded-full border-2 border-n-weak border-t-n-blue-9;
}
</style>
